// Partner-view hooks — the WRAPPER layer (lint-exempt for TanStack Query). The UI says "partner"; the code keeps
// "partner". PERSON-scoped (/me/partner/**): the partner holds no seat, so these never key on a band — the keys live under
// ['me','partner',…]. staleTime 0 + a refetch when the app comes back to the foreground (the spec's "no refresh" gap:
// their member may have just added them to a list while the app sat open). They are server-confined to these routes.
import { useQuery } from "@tanstack/react-query";
import type { components } from "@cntrl/api-client";
import { useApp } from "../appContext.js";
import { ApiError } from "./apiError.js";

export type PartnerView = components["schemas"]["PartnerView"];
export type PartnerViewItem = components["schemas"]["PartnerViewItem"];
export type PartnerShowDetail = components["schemas"]["PartnerShowDetail"];
export type PartnerTravelRow = components["schemas"]["PartnerTravelRow"];

/** `GET /me/partner/view` — the whole partner screen: whose logistics these are, and every upcoming date the member is on,
 *  across every band they are in, each show carrying the partner's own on-the-list status (912). */
export function usePartnerView() {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me", "partner", "view"],
    queryFn: async () => {
      const { data, error, response } = await api.GET("/api/v1/me/partner/view");
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    staleTime: 0,
    refetchOnWindowFocus: "always",
  });
}

/** `GET /me/partner/shows/{showId}` — one show the member is on, opened: their band's times, their travel, the partner's status.
 *  `null` for a show the member is no longer on (the server's 404) — the detail closes rather than erroring. */
export function usePartnerShow(showId: string | undefined) {
  const { api } = useApp();
  return useQuery({
    queryKey: ["me", "partner", "show", showId ?? "none"],
    queryFn: async (): Promise<PartnerShowDetail | null> => {
      const { data, error, response } = await api.GET("/api/v1/me/partner/shows/{showId}", {
        params: { path: { showId: showId! } },
      });
      if (response.status === 404) return null;
      if (!response.ok || !data) throw new ApiError(response.status, error);
      return data;
    },
    enabled: showId !== undefined,
    staleTime: 0,
  });
}
